import React from 'react';
import { fromPairs, map, find, head, get, isEmpty, isEqual, } from 'lodash';
import classnames from 'classnames';

// local dependencies
import { withNetworkState } from 'ui/hoc/withNetworkState';
import { Form } from 'modules/form';
import { button as Button } from 'modules/button';
import { getLabel } from 'utils/getLabel';
import { Typeahead } from 'modules/typeahead';
import { MatchCriteria } from 'modules/matchCriteria';
import { widgetPreloader as WidgetPreloader } from 'ui/views/generic/widgetPreloader';
import { dimensions } from 'config/dimensions/pharmacy-auth-criteria-grid';
import { inputField as InputField } from 'ui/views/generic/inputField';
import { DATA_TYPES, /**GLOBAL_LABELS */ } from 'constants/common';

//import { radioList as RadioList } from 'ui/views/generic/radioList';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { authFlowCallerIdentificationDoctor as AuthFlowCallerIdentificationDoctor } from 'ui/views/widgets/authFlowCallerIdentificationDoctor';
 *
 * // render
 * <AuthFlowCallerIdentificationDoctor propName='propValue'/>
 */

// caller detail section (editable)
const CallerDetailSectionEditable = ( props ) => {

	const callerDetails = props.componentData.sections.callerDetails;
	const fieldsData = callerDetails.fields;

	// extracting actions
	const cancelAction = find( callerDetails.actions, { name: 'cancel' } );
	const saveAction = find( callerDetails.actions, { name: 'save' } );

	return (
		<div className='ui-auth-flow-caller-identification-doctor__caller-details'>
			<div className='ui-auth-flow-caller-identification-doctor__caller-details__title'>
				{ getLabel( callerDetails.title ) }
			</div>

			<Form
				fields={ fromPairs( map( fieldsData, ( field ) => [ field.name, field ] ) ) } // convert to object as specified by `Form` prop
				values={ props.doctorInfo } // default values
				isValid={ false }
				mode={ props.mode }
				onSubmit={ ( { values, isValid } ) => {
					if ( isValid ) {
						props.onFormSubmit( {
							memberId: props.editItemId,
							...values
						} );
					}
				} }
			>
				{ ( { fields, values, isValid, errors, onChange, onSumbit } = {} ) => {
					const classNameField = classnames( {
						'ui-auth-flow-caller-identification-doctor__caller-details__detailsForm__fields-inputEditable': ! isEqual( props.mode, 'readonly' ),
						'ui-auth-flow-caller-identification-doctor__caller-details__detailsForm__fields-inputReadonly': isEqual( props.mode, 'readonly' )
					} );

					return (
						<form
							onSubmit={ onSumbit }
							className='ui-auth-flow-caller-identification-doctor__caller-details__detailsForm'
						>
							{ /* fields */ }
							{ map( fields, ( field ) => {
								return (
									<field.view
										key={ field.name }
										className={ classNameField }
										name={ field.name }
										required={ field.required }
										placeholder={ field.placeholder }
										label={ field.title }
										onChange={ onChange }
										value={ values[ field.name ] }
										error={ errors[ field.name ] }
										mode={ props.mode }
									/>
								);
							} ) }

							{ /* submit/edit action */ }
							<Button
								id={ saveAction.name }
								disabled={ !isValid }
								type='submit'
								size='large'
								label={ saveAction.title }
								className='ui-auth-flow-caller-identification-doctor__caller-details__detailsForm__fields-button'
							/>

							{/* cancel action */}
							{ isEmpty( props.doctorInfo ) || isEqual( props.mode, 'readonly' ) ? null : (
								<Button
									id={ cancelAction.name }
									size='large'
									outline={ true }
									title={ cancelAction.title }
									className='ui-auth-flow-caller-identification-doctor__caller-details__detailsForm__fields-button'
									onClick={ () => props.onToggleEditMode( false ) }
								/>
							) }
						</form>
					);
				} }
			</Form>
		</div>
	);
};

// set display name
CallerDetailSectionEditable.displayName = 'authFlowCallerIdentificationDoctorSuccess.CallerDetailSectionEditable';

/* ------------------ */

// caller detail section (readonly)
const CallerDetailSectionReadOnly = ( props ) => {

	const callerDetails = props.componentData.sections.callerDetails;
	const fieldsData = callerDetails.fields;

	// extracting actions
	const editAction = find( callerDetails.actions, { name: 'edit' } );

	return (
		<div className='ui-auth-flow-caller-identification-doctor__caller-details'>
			<div className='ui-auth-flow-caller-identification-doctor__caller-details__title'>
				{ getLabel( callerDetails.title ) }
			</div>
			
			<div className='ui-auth-flow-caller-identification-doctor__caller-details__readonly-view'>
				{ /* fields */ }
				{ map( fieldsData, ( field ) => {
					return (
						<InputField
							key={ field.name }
							className='ui-auth-flow-caller-identification-doctor__caller-details__readonly-view__field'
							name={ field.name }
							label={ field.title }
							value={ props.doctorInfo[ field.name ] }
							mode='readonly'
						/>
					);
				} ) }

				{/* edit action */}
				<Button
					size='large'
					title={ editAction.title }
					onClick={ () => {
						props.onToggleEditMode( true  );
					} }
				/>
			</div>
		</div>
	);
};

// set display name
CallerDetailSectionReadOnly.displayName = 'authFlowCallerIdentificationDoctorSuccess.CallerDetailSectionReadOnly';

/* ------------------ */

// doctor search section
const DoctorSearch = ( props ) => {

	// extracting caller identification details and its fields
	const callerIdentification = get( props, 'componentData.sections.callerIdentification', {} );
	const callerIdentificationFields = get( callerIdentification, 'fields', [] );
	
	// get search type field
	const searchTypeField = find( callerIdentificationFields, { type: DATA_TYPES.RADIO_LIST } );
	
	//const searchTypeOptions = get( props, 'componentData.options.doctorSearchTypes', [] );

	return (
		<div className='ui-auth-flow-caller-identification-doctor__doctor-search'>
			{
				map( callerIdentificationFields, ( field ) => {

					// if( DATA_TYPES.RADIO_LIST === field.type ) {
					// 	return (
					// 		<div
					// 			key={ field.name }
					// 			className='ui-auth-flow-caller-identification-doctor__doctor-search__field'
					// 		>
					// 			<RadioList
					// 				options={ searchTypeOptions }
					// 				value={ props.doctorSearchType }
					// 				onChange={ props.onDoctorSearchTypeChange }
					// 			/>
					// 		</div>
					// 	);
					//}

					if( DATA_TYPES.SEARCH === field.type ) {

						// request payload
						const requestPayload = ! searchTypeField ? {} : {
							data: {
								[ searchTypeField.name ]: props.doctorSearchType
							}
						};

						return (
							<div
								key={ field.name }
								className='ui-auth-flow-caller-identification-doctor__doctor-search__field'
							>
								<Typeahead
									className='field-value-search__typeahead'
									size='large'
									url={ get( field, 'url' ) }

									// placeholder={isEmpty( props.doctorSearchType ) ? { refId: GLOBAL_LABELS.DISPLAY_TEXT_TYPE_TO_SEARCH, value: '' } : {
									// 	refId: '',
									// 	value: getLabel( field.placeholder, { searchType: props.doctorSearchType } ),
									// }

									placeholder={ field.placeholder }

									//disabled={ searchTypeField ? isEmpty( props.doctorSearchType ) : false }
									onQueryChange={ props.onDoctorSearchQueryChange }
									requestPayload={ requestPayload }
									onChange={ ( { selected } ) => {
									props.onSelectDoctorSearch( head( selected ) ); // update selected value
									} }
								/>
							</div>
						);
					} else {
						return null;
					}
				} )
			}
		</div>
	);
};

// set display name
DoctorSearch.displayName = 'authFlowCallerIdentificationDoctorSuccess.DoctorSearch';

/* ------------------- */

// doctor match criteria section
const DoctorCriteria = ( props ) => {

	// extract actions for next button
	const actions = get( props.dataForMatchCriteria, 'actions' );
	const action = find( actions, { name: 'nextInbound' } );

	return (
		<div className='ui-auth-flow-caller-identification-doctor__callerVerification'>
			{ /* Match Criteria Div */ }
			<div className='ui-auth-flow-caller-identification-doctor__callerVerification__matchCriteria'>
				<MatchCriteria
					onMarkVerified={ props.handleMarkVerifiedAndShowNext }
					data={ props.dataForMatchCriteria }
					saveCriteriaSelection={ props.saveCriteriaSelection }
					layout={ dimensions }
				/>
			</div>
			
			{ /* the partition */ }
			<div className='ui-auth-flow-caller-identification-doctor__callerVerification__partition'></div>

			{ /* the next button */ }
			<Button
				className='ui-auth-flow-caller-identification-doctor__callerVerification__action'
				size='large'
				title={ action.title }
				disabled={ ! props.isMemberVerified }
				postIconId='arrow'
				showPostIcon={true}
				iconWidth='20'
				iconHeight='30'
				onClick={ () => props.handleActionClick( {
					item: props.doctorInfo,
					value: props.actionsForNext,
				} ) }
			/>
		</div>
	);
};

// set display name
DoctorCriteria.displayName = 'authFlowCallerIdentificationDoctorSuccess.DoctorCriteria';

/*---------------------- */

export const authFlowCallerIdentificationDoctorSuccess = ( props ) => {

    // dynamic class name
	const widgetClassName = classnames( 'ui-auth-flow-caller-identification-doctor', props.className );
	
	return (
		<div className={ widgetClassName }>
	
			{ /* Caller Details form */ }
			{
				isEqual( props.mode, 'readonly' ) ? (
					<CallerDetailSectionReadOnly { ...props } />
				) : (
					<CallerDetailSectionEditable { ...props } />
				)
			}

			{ /* Search NPI */ }
			{ ! props.isSearchDoctorVisible ? null : <DoctorSearch { ...props } /> }
			
			{ /* Match Criteria along with next button */ }
			{ ! props.isCriteriaVisible ? null : <DoctorCriteria { ...props } /> }

		</div>
    );
};

// set display name
authFlowCallerIdentificationDoctorSuccess.displayName = 'authFlowCallerIdentificationDoctorSuccess';

// set default props
authFlowCallerIdentificationDoctorSuccess.defaultProps = {};

// prop types
authFlowCallerIdentificationDoctorSuccess.propTypes = {};

/* ******************************************************* */

// preloader view
export const authFlowCallerIdentificationDoctorPreloader = () => {
	return (
		<div className='ui-auth-flow-caller-identification-doctor-preloader'>
			<WidgetPreloader />
		</div>
	);
 };

// set display name
authFlowCallerIdentificationDoctorPreloader.displayName = 'authFlowCallerIdentificationDoctorPreloader';

// set default props
authFlowCallerIdentificationDoctorPreloader.defaultProps = {};

// prop types
authFlowCallerIdentificationDoctorPreloader.propTypes = {};

/* ********************************************************* */

export const authFlowCallerIdentificationDoctor = withNetworkState(
	authFlowCallerIdentificationDoctorPreloader,
	authFlowCallerIdentificationDoctorSuccess,
);
